import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useTema, TemaProvider } from './hooks/useTema';
import AuthPage        from './pages/AuthPage';
import HomePage        from './pages/HomePage';
import GuidaPage       from './pages/GuidaPage';
import AboutPage       from './pages/AboutPage';
// Mancava (2026-09-13). BlogPage veniva USATA a fine file ma non importata:
// aprire /blog dava "ReferenceError: BlogPage is not defined" e una pagina
// bianca. Insieme alla voce mancante in pathToPage, e' il motivo per cui dal
// 30 agosto il blog non era raggiungibile in nessun modo da un indirizzo.
import BlogPage        from './pages/BlogPage';
import TermsPage       from './pages/TermsPage';
import PrivacyPage     from './pages/PrivacyPage';
import PostRegisterUpsellPage from './pages/PostRegisterUpsellPage';
import AutoCheckoutPage from './pages/AutoCheckoutPage';
import VaultPage       from './pages/VaultPage';
import PlansPage       from './pages/PlansPage';
import AdminPanel      from './pages/AdminPanel';
import ProfilePage     from './pages/ProfilePage';
import UnlockModal     from './pages/UnlockModal';
import { PaymentSuccessPage, PaymentCancelPage } from './pages/PaymentPages';
import FamilyAcceptPage from './pages/FamilyAcceptPage';
import InstallPrompt from './components/InstallPrompt';
import EmailVerifyPage from './pages/EmailVerifyPage';
import CheckoutBridgePage from './pages/CheckoutBridgePage';
import NewsPage        from './pages/NewsPage';
import StackSocialPage from './pages/StackSocialPage';
import SharePage       from './pages/SharePage';
import { captureAttribution, captureReferral } from './utils/attribution';
import './App.css';
import { forgetNativeBackupKey } from './utils/nativeBackup';

// Capture utm_source/utm_medium/utm_campaign/fbclid/rdt_cid from the landing
// URL once, before any client-side navigation changes it — see utils/attribution.js.
captureAttribution();
// Capture ?ref=CODE (referral program) the same way — first touch wins.
captureReferral();

// Generate session ID once per browser session. Runs at module load, before
// React has mounted anything — an uncaught throw here (some in-app browsers
// block or throw on sessionStorage access) would break the entire app before
// a single pixel renders, not just this one feature. Falls back to a
// per-load id (never persisted, but harmless — this is only used to group
// analytics events, not anything functional) instead of crashing. Found
// 2026-08-27 auditing every localStorage/sessionStorage call after a
// different one (hooks/usePricing.js) turned out to silently hide the promo
// banner in Facebook/Instagram's in-app browser.
const SESSION_ID = (() => {
  try {
    let id = sessionStorage.getItem('lc_sid');
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('lc_sid', id); }
    return id;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
})();

// Track page visit. loggedIn/shown are passed in explicitly (rather than
// read from some shared auth state here) because this is a module-level
// function, not a hook — only the caller, inside the App component, has
// isAuthenticated etc.
//
// `shown` (2026-08-30) — real bug found live: the admin Live tab used to
// show only the URL `page` plus the loggedIn boolean, which reads as "this
// visitor is looking at whatever page this path normally means". A user who
// had just paid, but whose session hadn't finished resolving yet (see
// resolveShown below), showed up as "/register, anonimo" — indistinguishable
// from an actual stranger looking at the sign-up form. What they were
// ACTUALLY looking at was the unlock-password screen. `shown` is the
// resolved component identity (see resolveShown), always sent alongside the
// raw path so the Live tab can report what was really on screen instead of
// inferring it from the URL.
//
// Il token viene allegato quando c'e' (2026-09-01) per il filtro "Escludi
// clienti paganti" della tab Live: il server lo decodifica in modo
// FACOLTATIVO e ne ricava solo 'anon' | 'free' | 'paid' | 'admin'. Senza
// token la chiamata resta identica a prima ed e' anonima — l'endpoint non e'
// autenticato e non deve diventarlo, altrimenti sparirebbero dalle
// statistiche proprio i visitatori anonimi.
// utm_source della visita: prima dall'indirizzo corrente, poi
// dall'attribuzione messa da parte all'ingresso (attribution-capture.js la
// salva quando il visitatore arriva). Tutto in try/catch: un'eccezione qui
// impedirebbe il conteggio della visita, che e' peggio del dato mancante.
function leggiUtmSource() {
  try {
    const u = new URLSearchParams(window.location.search).get('utm_source');
    if (u) return u.slice(0, 40);
  } catch {}
  try {
    const grezzo = localStorage.getItem('lc_attribution');
    if (grezzo) {
      const a = JSON.parse(grezzo);
      if (a && a.utmSource) return String(a.utmSource).slice(0, 40);
      if (a && a.utm_source) return String(a.utm_source).slice(0, 40);
    }
  } catch {}
  return '';
}

function trackVisit(page, loggedIn = false, shown = null) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('zv_token');
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {}
    fetch('/api/analytics/visit', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        page,
        referrer: document.referrer,
        country: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sessionId: SESSION_ID,
        loggedIn,
        shown,
        // Provenienza dichiarata dalla pagina di arrivo (2026-09-02): il
        // parametro c'era nell'indirizzo ma non arrivava mai al pannello.
        // Si legge dall'attribuzione gia' catturata all'ingresso, cosi' vale
        // anche per le pagine successive della stessa visita, non solo per la
        // prima. Solo utm_source: e' un'etichetta di campagna nostra.
        utmSource: leggiUtmSource()
      })
    }).catch(() => {});
  } catch {}
}

// Mirrors AppRouter's own routing decisions closely enough to know which
// actual screen a given state combination renders — not a byte-for-byte
// duplicate of every branch below (some fall through to the same handful of
// outcomes anyway), just enough resolution to tell apart the cases that
// matter for the Live tab: a real auth/register form vs. the unlock-password
// screen vs. the 2FA screen vs. the vault vs. the anonymous marketing pages.
// LE PAGINE CHE SI LEGGONO SEMPRE (2026-09-13).
//
// Blog, chi siamo, novita', termini e privacy non dipendono dall'avere un
// account: chi ha fatto l'accesso ha lo stesso diritto di leggerle di chi non
// ce l'ha. Stanno in un elenco solo perche' i punti che devono conoscerle sono
// tre — quale schermata mostrare, il rimando automatico dell'amministratore al
// pannello, e il rendering — e finche' erano tre elenchi scritti a mano hanno
// divergono: /blog non era in nessuno dei tre, e /terms mancava dal secondo.
const PAGINE_PUBBLICHE = ['blog', 'about', 'news', 'terms', 'privacy', 'stacksocial'];

function resolveShown({ page, isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession }) {
  if (!isAuthenticated) {
    if (pending2fa) return 'twofa';
    if (needsUnlock && hasStoredSession) return 'unlock';
    if (page === 'payment-success') return 'payment-success';
    if (page === 'payment-cancel') return 'payment-cancel';
    if (page === 'auth' || page === 'login' || page === 'register') return 'auth-form';
    if (PAGINE_PUBBLICHE.includes(page)) return page;
    return 'home-anonymous';
  }
  if (page === 'payment-success') return 'payment-success';
  if (page === 'payment-cancel') return 'payment-cancel';
  if (page === 'post-register') return 'post-register';
  if (page === 'plans') return 'plans';
  if (page === 'profile') return 'profile';
  if (page === 'admin' && isAdmin) return 'admin';
  // LE PAGINE PUBBLICHE VALGONO ANCHE DA DENTRO (2026-09-13).
  //
  // Questa riga stava SOTTO il `if (isAdmin) return 'admin'` qui sopra, quindi
  // per un amministratore non veniva mai raggiunta: aprire /terms o /privacy
  // portava al pannello. E 'blog' non era nell'elenco affatto, quindi un utente
  // connesso che apriva /blog finiva nel vault — o, da amministratore, nel
  // pannello. Il blog si vedeva solo da sloggati, ed e' per questo che sembrava
  // un rimando alla home.
  //
  // Sono pagine pubbliche: chi ha fatto l'accesso ha lo stesso diritto di
  // leggerle di chi non ce l'ha. Va PRIMA dei due rami che dirottano tutto.
  if (PAGINE_PUBBLICHE.includes(page)) return page;
  if (isAdmin) return 'admin';
  return 'vault';
}

function pathToPage(path) {
  if (path.includes('/payment/success')) return 'payment-success';
  if (path.includes('/payment/cancel'))  return 'payment-cancel';
  if (path.startsWith('/family/accept')) return 'family-accept';
  if (path.startsWith('/s/'))            return 'share-view';
  if (path === '/verify-email')          return 'verify-email';
  if (path === '/checkout')              return 'checkout-bridge';
  if (path === '/plans')    return 'plans';
  if (path === '/about')    return 'about';
  // Le guide: /guida/mac e /guida/windows (con /guide/... come alias inglese).
  // Il PIANO della guida sta nell'indirizzo, la LINGUA no: la decide
  // l'interruttore IT/EN del sito, come per ogni altra pagina.
  if (/^\/(guida|guide)\/(mac|windows)$/.test(path)) return 'guida';
  // /blog non c'era, e per questo dal 30 agosto quell'indirizzo apriva la home:
  // i 53 articoli statici erano stati tolti da public/blog "perche' il blog vive
  // in BlogPage.jsx" (25b32f7), ma nessuno ha aggiunto la riga che porta li'.
  // I dieci articoli della BlogPage erano raggiungibili solo navigando dentro
  // l'applicazione, mai da un indirizzo.
  if (path === '/blog')     return 'blog';
  if (path === '/news')     return 'news';
  if (path === '/stacksocial') return 'stacksocial';
  if (path === '/terms')    return 'terms';
  if (path === '/privacy')  return 'privacy';
  if (path === '/admin')    return 'admin';
  if (path === '/profile')  return 'profile';
  if (path === '/register') return 'register';
  if (path === '/login')    return 'login';
  return 'vault';
}

function AppRouterInterno({ onPagina }) {
  const { isAuthenticated, isAdmin, restoring, restoreError, retryRestore, needsUnlock, hasStoredSession, pending2fa, clearSession } = useAuth();
  const [page, setPage] = useState(() => pathToPage(window.location.pathname));

  // La pagina corrente serve al guscio, che decide tema e ambito (vedi
  // GuscioTema piu' sotto). Si riporta invece di far dedurre l'informazione
  // dall'URL: `page` e' la verita', l'URL a volte la insegue (pushState +
  // setPage, /plans per un anonimo che in realta' rende la home).
  // Si riporta un BOOLEANO deciso qui, non il nome della pagina: il nome da
  // solo non basta a dire se la vista e' pubblica o interna, e credevo di si'.
  // pathToPage('/') restituisce 'vault', non 'home' — la home pubblica e' il
  // ramo `page === 'vault'` di un utente NON autenticato. Con un elenco di nomi
  // il guscio classificava la home come area interna e la forzava scura.
  // Le uniche viste interne sono queste quattro, e solo da autenticati.
  const vistaInterna = isAuthenticated
    && (page === 'vault' || page === 'admin' || page === 'profile' || page === 'plans');
  // Si riporta anche SE SIAMO SULLA HOME: li' la fascia in cima non va piu'
  // resa, perche' l'offerta vive nella card ancorata in basso (2026-09-06).
  // "DENTRO L'APP" (2026-09-16) — un segnale diverso da vistaInterna, che
  // serve al TEMA e resta com'e'. vistaInterna e' falso subito dopo il login
  // (page resta 'login' mentre si rende gia' il vault): per decidere se
  // mostrare cose da "fuori" (la riga Install app) serve sapere se l'utente
  // autenticato sta vedendo il prodotto, qualunque chiave abbia la pagina.
  const inApp = isAuthenticated
    && ['vault', 'login', 'register', 'auth', 'admin', 'profile', 'plans', 'post-register'].includes(page);
  useEffect(() => { if (onPagina) onPagina(vistaInterna, inApp); }, [vistaInterna, inApp, onPagina]);
  // Captured once, synchronously, at the same time as `page` — the history-seeding
  // effect below rewrites the URL to "/" shortly after mount, which would otherwise
  // wipe the /s/<id>#k=<key> share link before SharePage ever gets to read it.
  const [shareLinkParams] = useState(() => {
    const m = window.location.pathname.match(/\/s\/([^/?#]+)/);
    if (!m) return null;
    const k = window.location.hash.match(/k=([^&]+)/);
    return { token: m[1], fragmentKey: k ? decodeURIComponent(k[1]) : null };
  });
  // Same capture-before-the-wipe-effect pattern as shareLinkParams above —
  // opening a real /vault/:folderId link (or the browser restoring one after
  // a back-navigation reload) needs this before the history-seeding effect
  // below rewrites the URL.
  const [initialFolderId] = useState(() => {
    const m = window.location.pathname.match(/^\/vault\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });

  // Fixed destination, no branching on whatever page/session state preceded
  // it — replaceState (not pushState) so logging out doesn't leave the
  // just-exited authenticated page reachable via the back button.
  const handleLogout = useCallback(() => {
    // LA CHIAVE DEL BACKUP SE NE VA CON LA SESSIONE (2026-09-12).
    // Dentro l'app Android il backup foto conserva la chiave principale nel
    // Keystore per poter lavorare a telefono chiuso. Uscire dall'account e
    // lasciarla li' significherebbe lasciare la chiave dei file su un
    // telefono che magari non e' piu' in mano al proprietario.
    // A vuoto e senza await: fuori dall'app non esiste nessun plugin e la
    // funzione risponde "non disponibile"; se fallisse, l'uscita non deve
    // fermarsi per questo.
    forgetNativeBackupKey().catch(() => {});
    clearSession();
    window.history.replaceState({ page: 'vault' }, '', '/');
    setPage('vault');
    trackVisit('/', false, 'home-anonymous');
  }, [clearSession]);

  const navigate = useCallback((newPage) => {
    const url = newPage === 'vault' ? '/' : `/${newPage}`;
    window.history.pushState({ page: newPage }, '', url);
    setPage(newPage);
    trackVisit(url, isAuthenticated, resolveShown({ page: newPage, isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession }));
  }, [isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession]);

  // Email captured in the homepage hero's signup box, carried to /register as
  // navigation state (history.state, not a query string) so it survives only
  // within this SPA session — a direct link, a refresh, or a shared /register
  // URL never has it, by construction (see AuthPage's initialEmail prop).
  // Every plain "Register" entry point (nav bar, plans, footer, About/News
  // pages) goes through this same function with no email argument, which
  // resets it to null — so a stale hero email can never leak into a later
  // plain-register visit in the same session.
  //
  // Lazy-initialized from window.history.state (2026-08-28) rather than a
  // bare `useState(null)` — a plain reload while on /register re-runs this
  // whole component from scratch, and React state doesn't survive that, but
  // the pushState'd history entry itself does (a browser reload keeps
  // history.state for the current entry). Without this, reloading mid-
  // registration silently dropped a hero_buy intent — confirmed live,
  // 2026-08-28, the exact bug this fixes.
  const [prefillEmail, setPrefillEmail] = useState(() => window.history.state?.prefillEmail || null);
  // buyIntent: same lifecycle/reset rule as prefillEmail above — an
  // {plan, campaign} object when set (2026-08-28, generalized from the
  // original hero-only 'hero_buy' string to cover every purchase entry
  // point: hero, plans-section cards, promo banner), tells the post-
  // registration step to skip the free/upgrade choice entirely and go
  // straight to Stripe checkout for that plan, tagged with that campaign
  // (see the page==='post-register' branch below), instead of showing
  // PostRegisterUpsellPage tagged post_register. Every plain registration
  // entry point still calls goToRegister() with no second argument, which
  // resets this to null.
  const [buyIntent, setBuyIntent] = useState(() => window.history.state?.buyIntent || null);
  // entrata: QUALE elemento del sito ha aperto la registrazione (slug corto:
  // 'hero_email', 'card_free', 'nav', 'piede', 'da_login', 'fascia_offerta',
  // o la campagna del pulsante d'acquisto). Solo misurazione — non cambia
  // nessun comportamento. Salvata dal server in users.register_entry.
  const [entrata, setEntrata] = useState(() => window.history.state?.entrata || null);
  const goToRegister = useCallback((email, intent, daDove) => {
    const emailState = email || null;
    const intentState = intent || null;
    const entrataState = daDove || intentState?.campaign || null;
    window.history.pushState({ page: 'register', prefillEmail: emailState, buyIntent: intentState, entrata: entrataState }, '', '/register');
    setPage('register');
    setPrefillEmail(emailState);
    setBuyIntent(intentState);
    setEntrata(entrataState);
    trackVisit('/register', isAuthenticated, resolveShown({ page: 'register', isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession }));
  }, [isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession]);

  // Navigate back to the home page, optionally deep-linking to one of its sections
  // Apre una guida senza ricaricare la pagina. L'indirizzo italiano e' quello
  // canonico; /guide/... resta valido come alias e porta alla stessa pagina.
  const apriGuida = useCallback((quale) => {
    window.history.pushState({ page: 'guida' }, '', `/guida/${quale}`);
    setPage('guida');
    window.scrollTo(0, 0);
  }, []);

  const goHome = useCallback((hash) => {
    const url = (typeof hash === 'string' && hash) ? `/#${hash}` : '/';
    window.history.pushState({ page: 'vault' }, '', url);
    setPage('vault');
    trackVisit(url, isAuthenticated, resolveShown({ page: 'vault', isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession }));
  }, [isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession]);

  // Track initial page visit — waits for auth state to actually settle
  // before sending anything (2026-08-30, real bug found live: this used to
  // fire once, unconditionally, on the very first render — which happens
  // BEFORE useAuth's own restore effect runs (child effects fire before
  // parent effects, and AuthProvider is the parent here), so on a fresh
  // page load isAuthenticated/needsUnlock were still both false, their
  // initial values, indistinguishable from "genuinely logged out". A user
  // whose session needed unlocking got logged as a plain anonymous visitor
  // on whatever URL they landed on — which is exactly what made a paying
  // customer look, in the Live tab, like a stranger looking at the sign-up
  // form. Now waits for a real signal: either there's no stored session at
  // all (nothing to wait for, track immediately) or one of
  // restoring/needsUnlock/isAuthenticated/pending2fa has actually been set
  // by useAuth's effect.
  const initialTracked = useRef(false);
  useEffect(() => {
    if (initialTracked.current) return;
    const ambiguous = hasStoredSession && !restoring && !needsUnlock && !isAuthenticated && !pending2fa;
    if (ambiguous) return; // useAuth hasn't resolved anything yet — wait one more tick
    initialTracked.current = true;
    const initialPage = pathToPage(window.location.pathname);
    trackVisit(window.location.pathname || '/', isAuthenticated, resolveShown({ page: initialPage, isAuthenticated, isAdmin, needsUnlock, pending2fa, hasStoredSession }));
  }, [restoring, needsUnlock, isAuthenticated, pending2fa, hasStoredSession, isAdmin]);

  useEffect(() => {
    // Seed history with multiple vault entries as buffer — but only when we're
    // actually landing on the vault. Doing this unconditionally used to rewrite
    // the URL to "/" on every mount, even for direct deep links like /plans,
    // /about or /news: the right page still rendered (page state was already
    // captured above) but the address bar snapped back to "/", which reads as
    // "it redirected to the home page".
    // A /vault/:folderId deep link keeps that folder in the URL instead of
    // being wiped to "/" here — same reason initialFolderId is captured
    // above, before this runs. The buffer itself is still seeded (protects
    // against one back-tap exiting the app), just anchored at that folder
    // instead of the root.
    if (page === 'vault') {
      const vaultUrl = initialFolderId ? `/vault/${initialFolderId}` : '/';
      const vaultState = { page: 'vault', folderId: initialFolderId || undefined };
      window.history.replaceState(vaultState, '', vaultUrl);
      for (let i = 0; i < 5; i++) {
        window.history.pushState(vaultState, '', vaultUrl);
      }
    }

    function onPopState(e) {
      const p = e.state?.page || pathToPage(window.location.pathname);
      setPage(p || 'vault');
      setPrefillEmail(e.state?.prefillEmail || null);
      setBuyIntent(e.state?.buyIntent || null);
      setEntrata(e.state?.entrata || null);
      // Re-add buffer entries when landing back on the true vault root —
      // but NOT when e.state.folderId is set: that's a legitimate
      // folder-level back-navigation (see useFiles.js's own popstate
      // listener, which handles loading that folder), and re-seeding here
      // would bury it under fresh "/" entries, breaking the very back
      // button this is supposed to make work.
      if ((!p || p === 'vault') && !e.state?.folderId) {
        for (let i = 0; i < 3; i++) {
          window.history.pushState({ page: 'vault' }, '', '/');
        }
      }
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line

  // Admin lands on AdminPanel regardless of which page was current when isAdmin
  // became true (e.g. right after login, page is still 'auth' at '/login'). Sync
  // the URL to /admin so it doesn't stay stuck on the stale path.
  useEffect(() => {
    if (isAuthenticated && isAdmin && page !== 'admin' && page !== 'profile' && page !== 'plans' && page !== 'post-register'
        && !PAGINE_PUBBLICHE.includes(page)) {
      navigate('admin');
    }
  }, [isAuthenticated, isAdmin, page, navigate]);

  // ── Loading ──────────────────────────────────────────────
  if (restoring) {
    return (
      <div className="restore-screen">
        <div className="restore-spinner">🔐</div>
        <p>Ripristino sessione…</p>
      </div>
    );
  }

  // Session restore failed after retrying (2026-08-30) — network/timeout/
  // backend down, never a rejected token (that case clears the session and
  // falls through to the login/register screen below on purpose). The
  // token, user and session password are still intact at this point; this
  // is deliberately its own screen rather than the login form, so a
  // connectivity blip never reads as "your account/signup is gone".
  if (restoreError) {
    return (
      <div className="restore-screen">
        <div style={{ fontSize: 40 }}>⚠️</div>
        <p>Problema di connessione. Riprova.</p>
        <button className="btn-primary" onClick={retryRestore}>Riprova</button>
      </div>
    );
  }

  // Family invite acceptance — reachable whether logged in or not
  if (page === 'family-accept') return <FamilyAcceptPage onDone={() => navigate('vault')} />;

  // Public share link — reachable whether logged in or not, no account needed
  if (page === 'share-view') return <SharePage token={shareLinkParams?.token} fragmentKey={shareLinkParams?.fragmentKey} onHome={() => goHome()} />;

  // Email verification link — reachable whether logged in or not
  if (page === 'verify-email') return <EmailVerifyPage onDone={() => navigate('vault')} />;

  // In-app-browser checkout bridge link — reachable with NO session at all,
  // that's the whole point (see components/InAppBrowserWarning.jsx and
  // pages/CheckoutBridgePage.jsx). Redeems its own token server-side and
  // redirects straight to Stripe; onDone only fires on an expired/invalid
  // token, sending the visitor to the public home page (goHome already
  // resolves correctly for a logged-out visitor).
  if (page === 'checkout-bridge') return <CheckoutBridgePage onDone={goHome} />;

  // Legal pages — reachable whether logged in or not, same as the group above.
  if (page === 'blog')    return <BlogPage    onBack={goHome} />;
  if (page === 'terms')   return <TermsPage   onNavigateHome={goHome} onPrivacy={() => navigate('privacy')} />;
  if (page === 'privacy') return <PrivacyPage onNavigateHome={goHome} onTerms={() => navigate('terms')} />;

  // StackSocial redemption. History: this branch used to fire
  // unconditionally, which got stuck re-rendering "Creating your vault…"
  // forever once a redemption succeeded (isAuthenticated flips true inside
  // useAuth.js's redeemStackSocialCode, but `page` never changes away from
  // 'stacksocial' on its own). Fixed 2026-08-29 by gating on
  // !isAuthenticated — which introduced a WORSE bug, found live 2026-08-30:
  // ANY already-authenticated visitor landing on /stacksocial for ANY
  // reason (not just post-redemption) got silently dropped straight into
  // their vault with zero explanation of why the page they navigated to
  // never appeared.
  //
  // Fixed properly now: this branch fires unconditionally again (page ===
  // 'stacksocial' alone, same as terms/privacy above), and StackSocialPage
  // itself branches on auth state — not authenticated: the original
  // create-account form; authenticated + free plan: a simplified redeem-to-
  // this-account form; authenticated + already paid: a "you already have a
  // plan" message, no form, code never touched. All three are explicit
  // screens the visitor actually sees — none of them silently fall through
  // to the vault. onGoToVault is the one explicit, user-initiated exit into
  // the vault (a button after a successful self-redemption, or from the
  // already-paid message) — never automatic.
  if (page === 'stacksocial') return <StackSocialPage
    onNavigateHome={goHome}
    onTerms={() => navigate('terms')}
    onPrivacy={() => navigate('privacy')}
    onGoToVault={() => navigate('vault')}
  />;

  // LE GUIDE STANNO FUORI DAL RAMO "non autenticato" di proposito: i
  // collegamenti nel profilo puntano qui, e chi li clicca ha per forza una
  // sessione aperta. Dentro quel ramo la pagina sarebbe irraggiungibile
  // proprio per le persone a cui serve.
  if (page === 'guida') {
    const m = window.location.pathname.match(/^\/(?:guida|guide)\/(mac|windows)$/);
    return <GuidaPage
      slug={m ? m[1] : 'mac'}
      onApriGuida={apriGuida}
      onLogin={() => { window.history.pushState({ page: 'login' }, '', '/login'); setPage('login'); }}
      onRegister={() => goToRegister(null, null, 'nav')}
      onNavigateHome={goHome}
      onNews={() => { window.history.pushState({ page: 'news' }, '', '/news'); setPage('news'); }}
      onTerms={() => navigate('terms')}
      onPrivacy={() => navigate('privacy')}
    />;
  }

  // ── Not authenticated ────────────────────────────────────
  if (!isAuthenticated) {
    // LO SBLOCCO NON SEQUESTRA PIU' TUTTO IL SITO (2026-09-02).
    //
    // Questa riga stava PRIMA di ogni instradamento, quindi bastava avere
    // zv_token + zv_user in localStorage senza la password in sessionStorage
    // — cioe' aver chiuso la scheda, aperto una scheda nuova, riavviato il
    // browser, o essere tornati da un redirect esterno come Stripe — perche'
    // QUALUNQUE indirizzo mostrasse "Session locked": la home, i piani, la
    // registrazione, l'accesso.
    //
    // Misurato su visits.log (campo `shown`, che esiste dal 2026-08-30, quindi
    // il difetto e' probabilmente piu' vecchio di quanto si veda):
    //   253 volte in 5 giorni, ~70 persone distinte
    //   194 volte sulla HOME, 20 su /plans, 15 su /login, 4 su /register
    // Una di queste persone, oggi, ci ha girato dentro un'ora e mezza dopo
    // aver annullato un pagamento.
    //
    // Ora lo sblocco compare SOLO dove la chiave di cifratura serve davvero:
    // il vault e le aree operative. Sulle pagine pubbliche il visitatore vede
    // la pagina, e se vuole rientrare usa "Accedi" — che ora funziona, perche'
    // 'login' non e' piu' sequestrato.
    // ATTENZIONE al nome 'vault': pathToPage('/') restituisce 'vault', quindi
    // dentro questo ramo (!isAuthenticated) `page === 'vault'` e' la HOME
    // PUBBLICA, non il file manager — il file manager qui non si rende mai.
    // Mettere 'vault' nell'elenco avrebbe lasciato la home sequestrata, che e'
    // il caso piu' frequente dei 253 misurati (194 sulla home). E' la stessa
    // trappola del guscio del tema, poche ore fa.
    // Il vero "voglio entrare nel vault" si riconosce dal PERCORSO: un link
    // diretto a un file, /vault/<id>. Li' lo sblocco serve e resta.
    // Si usa initialFolderId, non window.location.pathname: l'indirizzo viene
    // riscritto a "/" subito dopo il montaggio (vedi l'effetto piu' sopra),
    // quindi al momento del render il percorso originale non c'e' piu'.
    // initialFolderId lo cattura AL MONTAGGIO, prima della riscrittura — e'
    // gia' li' apposta per questo.
    const chiedeIlVault = !!initialFolderId;
    const SERVE_LA_CHIAVE = new Set(['profile', 'admin', 'share-view']);
    if (needsUnlock && hasStoredSession && !pending2fa && (chiedeIlVault || SERVE_LA_CHIAVE.has(page))) {
      return <UnlockModal onLogout={handleLogout} />;
    }

    // Payment redirects — handle even when not authenticated
    if (page === 'payment-success') return <PaymentSuccessPage onDone={() => navigate('vault')} onLogout={handleLogout}
      onGoToLogin={(email) => {
      // L'email arriva dalla pagina di pagamento riuscito quando si e'
      // pagato attraverso il ponte, cioe' in un browser dove non si e'
      // mai fatto l'accesso: precompilarla evita di far ridigitare un
      // indirizzo creato un minuto prima, nel momento peggiore.
      window.history.pushState({ page: 'login', prefillEmail: email || null }, '', '/login');
      setPrefillEmail(email || null);
      setPage('login');
    }} />;
    if (page === 'payment-cancel')  return <PaymentCancelPage  onDone={() => navigate('vault')} />;

    // Auth pages — inside the native Android app, the marketing homepage
    // (HomePage below) is skipped entirely for a logged-out visitor: it's
    // built for someone landing from an ad/search result who needs to be
    // sold on the product, which is the wrong first screen for someone who
    // already installed the app. Login is shown directly instead (register
    // is one tap away via AuthPage's own toggle) — the web site itself is
    // untouched, this only fires when Capacitor.isNativePlatform() is true.
    if (page === 'auth' || page === 'login' || page === 'register' || (Capacitor.isNativePlatform() && page === 'vault')) {
      // Chi ha gia' una sessione su questo dispositivo e apre /register vede il
      // modulo di ACCESSO con una spiegazione, non quello di registrazione — e
      // mai piu' la schermata di sblocco, che fino al 2026-09-02 sequestrava
      // questo indirizzo insieme a tutti gli altri.
      //
      // MA MAI quando l'utente ha appena chiesto di registrarsi con un'email
      // precisa. Difetto vero, segnalato dal vivo e riprodotto il 2026-09-02:
      // nel browser interno di Facebook — che e' condiviso da chiunque usi quel
      // telefono, quindi una sessione salvata da una visita precedente c'e'
      // spesso — chi scriveva una email NUOVA nel modulo della hero si
      // ritrovava il modulo di accesso, con l'email buttata via. Le
      // registrazioni dagli annunci passano tutte di qui.
      //
      // forzaLogin TOLTA su /register (2026-09-05, passo 1): /register mostra
      // SEMPRE il modulo di registrazione, anche con una sessione lasciata sul
      // dispositivo — chi ha gia' un account usa il link "Accedi". Prima, per
      // una sessione residua senza intenzione d'acquisto (es. "Inizia gratis
      // con 1GB", che non porta ne' email ne' buyIntent), forzaLogin scattava
      // e mostrava il login al posto della registrazione: chi cliccava
      // "inizia gratis" si trovava un modulo di accesso per un account che non
      // ha. Confermato da due clienti. hasStoredSession non entra piu' in
      // questa decisione.
      return <AuthPage onHome={goHome} initialEmail={prefillEmail} initialBuyIntent={buyIntent} initialEntrata={entrata}
        forzaLogin={false}
        onSwitchToLogin={() => { window.history.pushState({ page: 'login' }, '', '/login'); setPage('login'); }}
        onSwitchToRegister={() => goToRegister(null, null, 'da_login')}
        onTerms={() => navigate('terms')}
        onPrivacy={() => navigate('privacy')}
        onRegistered={() => setPage(buyIntent?.plan ? 'post-register' : 'vault')} />;
    }

    // "/plans" isn't a real separate page for logged-out visitors — it's the
    // pricing section built into the homepage. No branch here on purpose:
    // this falls through to the HomePage return below, which scrolls to
    // #plans on mount when it detects this exact path (see HomePage.jsx).

    if (page === 'about') return <AboutPage
      onLogin={() => { window.history.pushState({ page: 'login' }, '', '/login'); setPage('login'); }}
      onRegister={() => goToRegister(null, null, 'nav')}
      onNavigateHome={goHome}
      onNews={() => { window.history.pushState({ page: 'news' }, '', '/news'); setPage('news'); }}
      onTerms={() => navigate('terms')}
      onPrivacy={() => navigate('privacy')}
    />;

    // onAbout e' nuova su questa pagina: la nav unica ha la voce "Chi siamo",
    // che su Novita' non e' mai esistita (verificato: nav_about non compare in
    // NewsPage.jsx in nessuno dei cinque commit che l'hanno toccata, dal 18
    // agosto). Senza la prop il bottone ci sarebbe ma non farebbe niente.
    if (page === 'news') return <NewsPage
      onApriGuida={apriGuida}
      onLogin={() => { window.history.pushState({ page: 'login' }, '', '/login'); setPage('login'); }}
      onRegister={() => goToRegister(null, null, 'nav')}
      onNavigateHome={goHome}
      onAbout={() => { window.history.pushState({ page: 'about' }, '', '/about'); setPage('about'); }}
      onTerms={() => navigate('terms')}
      onPrivacy={() => navigate('privacy')}
    />;

    // Home page
    return <HomePage
      onApriGuida={apriGuida}
      onLogin={() => { window.history.pushState({ page: 'login' }, '', '/login'); setPage('login'); }}
      onRegister={(daDove) => goToRegister(null, null, typeof daDove === 'string' ? daDove : 'nav')}
      onRegisterWithEmail={(email) => goToRegister(email, null, 'hero_email')}
      onRegisterBuyIntent={(intent, daDove) => goToRegister(null, intent, daDove)}
      onAbout={() => { window.history.pushState({ page: 'about' }, '', '/about'); setPage('about'); }}
      onNews={() => { window.history.pushState({ page: 'news' }, '', '/news'); setPage('news'); }}
      onTerms={() => navigate('terms')}
      onPrivacy={() => navigate('privacy')}
    />;
  }

  // ── Authenticated ────────────────────────────────────────
  if (page === 'payment-success') return <PaymentSuccessPage onDone={() => navigate('vault')} onLogout={handleLogout}
    onGoToLogin={(email) => {
      // L'email arriva dalla pagina di pagamento riuscito quando si e'
      // pagato attraverso il ponte, cioe' in un browser dove non si e'
      // mai fatto l'accesso: precompilarla evita di far ridigitare un
      // indirizzo creato un minuto prima, nel momento peggiore.
      window.history.pushState({ page: 'login', prefillEmail: email || null }, '', '/login');
      setPrefillEmail(email || null);
      setPage('login');
    }} />;
  if (page === 'payment-cancel')  return <PaymentCancelPage  onDone={() => navigate('vault')} />;
  // Post-registration step (2026-08-28, generalized same day) — shown
  // exactly once, right after a free signup, before the vault ever mounts.
  // Two different pages here on purpose: someone who already clicked a
  // "buy" button anywhere on the site (hero, plans-section cards, promo
  // banner — see goToRegister's callers) declared their intent before they
  // even had an account — showing them the same free/upgrade choice as
  // everyone else re-litigates a decision they already made, which is
  // exactly how an already-won sale gets lost. Any buyIntent set (an
  // {plan, campaign} object) skips straight to Stripe (AutoCheckoutPage);
  // plain registration (no intent) still sees the real choice
  // (PostRegisterUpsellPage). Either way, onContinueFree/onFallbackFree
  // just navigates to vault like any other page — VaultPage's own
  // sessionStorage check (zv_pending_recovery, set at registration) still
  // fires the recovery-key popup there, neither page touches that key.
  if (page === 'post-register') {
    if (buyIntent?.plan && buyIntent?.campaign) {
      return <AutoCheckoutPage plan={buyIntent.plan} campaign={buyIntent.campaign} onFallbackFree={() => navigate('vault')} />;
    }
    // Senza intenzione d'acquisto NON si arriva piu' qui (onRegistered manda
    // dritto al vault): la PostRegisterUpsellPage e' spenta per decisione del
    // 2026-09-04 — 5 su 6 sceglievano il gratis. Il ramo resta come ripiego
    // se qualcuno ci capita con la history.
    return <PostRegisterUpsellPage campaign="post_register" onContinueFree={() => navigate('vault')} />;
  }
  if (page === 'plans')   return <PlansPage   onBack={() => window.history.back()}
    onRegister={(plan) => goToRegister(null, { plan, campaign: plan === 'lifetime_2tb' ? 'plans_2tb' : 'plans_1tb' })} />;
  if (page === 'admin' && isAdmin) return <AdminPanel onBack={() => window.history.back()} />;
  if (page === 'profile') return <ProfilePage onApriGuida={apriGuida} onBack={() => window.history.back()} />;

  // Admin → always show admin panel directly
  if (isAdmin && page !== 'profile' && page !== 'plans') {
    return <AdminPanel onBack={() => navigate('admin')} onProfile={() => navigate('profile')} />;
  }

  // Default — vault for normal users
  return (
    <VaultPage
      onUpgrade={() => navigate('plans')}
      onAdmin={() => navigate('admin')}
      onProfile={() => navigate('profile')}
      onLogout={handleLogout}
      onTerms={() => navigate('terms')}
      onPrivacy={() => navigate('privacy')}
      initialFolderId={initialFolderId}
    />
  );
}


// ── Guscio del tema ────────────────────────────────────────────────────────
// Ultimo passo della migrazione al tema chiaro (2026-09-02): l'ambito
// .cps-home-root smette di stare su ogni singola pagina e passa qui, una volta
// sola. Le pagine pubbliche perdono la classe che si erano portate dietro una
// per una; il nome della classe resta quello per non riscrivere ~40 selettori
// gia' verificati.
//
// COSA RESTA FUORI, per decisione del 2026-09-02: vault, pannello admin,
// profilo e PlansPage non si migrano — sono aree operative, i loro colori fissi
// restano dove sono. Per loro il guscio forza data-tema="scuro", cosi' un
// utente che ha scelto il chiaro sul sito pubblico non si ritrova il file
// manager a meta' strada fra due palette.
//
// PlansPage e' fra le escluse anche se ci si arriva da /plans: quel
// percorso, per un visitatore non loggato, rende la HOME (vedi il commento piu'
// sopra). PlansPage la vedono solo utenti loggati che arrivano dal vault —
// verificato sul campo `shown` di visits.log: 38 rese in 7 giorni, tutte con
// loggedIn: true, zero anonime.
function GuscioTema({ interna, inApp = false, children }) {
  const { tema } = useTema();
  const pubblica = !interna;
  const temaEffettivo = pubblica ? tema : 'scuro';

  // Anche <body> deve seguire il tema, altrimenti il fondo scuro resta visibile
  // nel canale della barra di scorrimento e sotto il fondo pagina. Si scrive
  // sull'elemento invece di aggiungere una regola CSS che dipenda da :has(),
  // cosi' vale anche quando il guscio e' montato ma la pagina non ha ancora
  // dipinto niente.
  useEffect(() => {
    const b = document.body;
    const precedente = b.style.background;
    b.style.background = (pubblica && temaEffettivo !== 'scuro') ? '#eef0ee' : '';
    return () => { b.style.background = precedente; };
  }, [pubblica, temaEffettivo]);

  return (
    <div className={pubblica ? 'cps-guscio cps-home-root' : 'cps-guscio'} data-tema={temaEffettivo}>
      {/* Niente fascia dell'offerta in cima (2026-09-06). L'offerta ora e' la
          card ancorata in basso sulla home; sulle altre pagine pubbliche la
          fascia era rimasta accesa, e una striscia verde in cima a Termini,
          Privacy e al modulo di registrazione non aiuta nessuno a decidere —
          sulla pagina di registrazione e' anzi una distrazione a meta'
          percorso. Chi arriva da li' trova l'offerta tornando alla home. */}
      {children}
      {/* LA RIGA "INSTALL APP" STA DENTRO IL GUSCIO (2026-09-11).
          Era montata fuori, accanto ad AppRouter: i suoi colori venivano da
          :root, cioe' dalla palette scura, mentre la home in chiaro ridefinisce
          i propri token dentro .cps-home-root. Risultato misurato: riga con
          sfondo rgb(13,19,13) sopra una pagina con fondo rgb(238,240,238) —
          la striscia nera. Qui dentro eredita i token della pagina che la
          ospita, in chiaro come in scuro, senza colori scritti a mano. */}
      {/* NON DENTRO L'APP (2026-09-16): nel file manager (e nelle altre viste
          da loggato: profilo, admin, piani) la riga "Install app" non serve
          piu' — chi e' li' sta gia' usando il prodotto, e su telefono la
          pastiglia galleggiava sopra la griglia. Resta sulle pagine
          pubbliche (home, ecc.), dove ha ancora senso proporre l'app. */}
      {!inApp && <InstallPrompt />}
    </div>
  );
}

function AppRouter() {
  const [interna, setInterna] = useState(false);
  const [inApp, setInApp] = useState(false);
  const onPagina = useCallback((v, dentro = false) => { setInterna(v); setInApp(!!dentro); }, []);
  return (
    <GuscioTema interna={interna} inApp={inApp}>
      <AppRouterInterno onPagina={onPagina} />
    </GuscioTema>
  );
}

export default function App() {
  return (
    <AuthProvider>
      {/* Il tema e' uno stato solo per tutta l'applicazione: il guscio e le
          pagine lo leggono dallo stesso contesto. Vedi hooks/useTema.js per il
          difetto che nasceva da due useState separati. */}
      <TemaProvider>
        <AppRouter />
      </TemaProvider>
    </AuthProvider>
  );
}
